/**
 * Memoized selectors for crews
 * Uses createSelector for performance optimization
 */

import { createSelector } from '@reduxjs/toolkit';
import { transformCrewData, transformCrewList } from '../util/crewData';
import { selectAllBids } from './bids';
import { selectRawLinkups } from './linkups';

// Base selectors
export const selectCrewsState = state => state.crews || {};
export const selectRawUserCrews = createSelector(
  [selectCrewsState],
  crewsState => crewsState.userCrews || []
);
export const selectRawFeaturedCrews = createSelector(
  [selectCrewsState],
  crewsState => crewsState.featuredCrews || []
);
export const selectRawSearchResults = createSelector(
  [selectCrewsState],
  crewsState => crewsState.searchResults || []
);
export const selectRawUserMemberships = createSelector(
  [selectCrewsState],
  crewsState => crewsState.userMemberships || []
);
export const selectRawCrewDetails = (state, crewId) => state.crews?.crewDetails?.[crewId] || null;
export const selectCrewsLoading = createSelector(
  [selectCrewsState],
  crewsState => crewsState.fetchFeaturedCrewsInProgress || false
);
export const selectCrewsError = createSelector(
  [selectCrewsState],
  crewsState => crewsState.fetchFeaturedCrewsError || null
);
export const selectUserMembershipsLoading = createSelector(
  [selectCrewsState],
  crewsState => crewsState.fetchUserMembershipsInProgress || false
);
export const selectUserMembershipsError = createSelector(
  [selectCrewsState],
  crewsState => crewsState.fetchUserMembershipsError || null
);
export const selectCrewsMetadata = createSelector([selectCrewsState], crewsState => ({
  featuredCrewsLastFetched: crewsState.featuredCrewsLastFetched || null,
  featuredCrewsQueryKey: crewsState.featuredCrewsQueryKey || null,
  userMembershipsLastFetched: crewsState.userMembershipsLastFetched || null,
  userMembershipsUserId: crewsState.userMembershipsUserId || null,
}));

const formatMembership = membership => ({
  ...membership,
  formattedCreatedAt: membership?.created_at
    ? new Date(membership.created_at).toLocaleDateString()
    : null,
});

const enrichCrewsWithLiveCounts = (crews, linkups, bids) => {
  if (!Array.isArray(crews) || crews.length === 0) {
    return [];
  }

  const linkupCountByCrewId = {};
  const linkupCountByAttractionId = {};
  const linkupCountByAttractionName = {};
  (linkups || []).forEach(linkup => {
    const crewId = linkup?.crew_id;
    const attractionId = linkup?.attraction_id;
    const attractionName = linkup?.source_crew_name || linkup?.attraction_name;
    if (!crewId) {
      if (attractionId) {
        const attractionKey = String(attractionId);
        linkupCountByAttractionId[attractionKey] =
          (linkupCountByAttractionId[attractionKey] || 0) + 1;
      }
      if (attractionName) {
        const attractionNameKey = String(attractionName)
          .trim()
          .toLowerCase();
        linkupCountByAttractionName[attractionNameKey] =
          (linkupCountByAttractionName[attractionNameKey] || 0) + 1;
      }
      return;
    }

    const key = String(crewId);
    linkupCountByCrewId[key] = (linkupCountByCrewId[key] || 0) + 1;
    if (attractionId) {
      const attractionKey = String(attractionId);
      linkupCountByAttractionId[attractionKey] =
        (linkupCountByAttractionId[attractionKey] || 0) + 1;
    }
    if (attractionName) {
      const attractionNameKey = String(attractionName)
        .trim()
        .toLowerCase();
      linkupCountByAttractionName[attractionNameKey] =
        (linkupCountByAttractionName[attractionNameKey] || 0) + 1;
    }
  });

  const bidCountByCrewKey = {};
  (bids || []).forEach(bid => {
    const crewId = bid?.crewId || bid?.crew_id;
    const attractionId = bid?.attractionId || bid?.attraction_id;

    if (crewId) {
      const crewKey = `crew:${String(crewId)}`;
      bidCountByCrewKey[crewKey] = (bidCountByCrewKey[crewKey] || 0) + 1;
    }

    if (attractionId) {
      const attractionKey = `attraction:${String(attractionId)}`;
      bidCountByCrewKey[attractionKey] = (bidCountByCrewKey[attractionKey] || 0) + 1;
    }
  });

  return crews.map(crew => {
    const existingLinkups = Number(crew?.linkUpsCount ?? crew?.linkUps_nums ?? 0) || 0;
    const existingBids = Number(crew?.ticketBidsCount ?? crew?.ticket_bids_num ?? 0) || 0;
    const crewIdKey = `crew:${String(crew.id)}`;
    const attractionKey = crew?.attraction_id ? `attraction:${String(crew.attraction_id)}` : null;
    const liveLinkups = crew?.attraction_id
      ? Math.max(
          linkupCountByCrewId[String(crew.id)] || 0,
          linkupCountByAttractionId[String(crew.attraction_id)] || 0
        )
      : crew?.attraction_name
      ? Math.max(
          linkupCountByCrewId[String(crew.id)] || 0,
          linkupCountByAttractionName[
            String(crew.attraction_name)
              .trim()
              .toLowerCase()
          ] || 0
        )
      : linkupCountByCrewId[String(crew.id)] || 0;
    const liveBids = attractionKey
      ? Math.max(bidCountByCrewKey[crewIdKey] || 0, bidCountByCrewKey[attractionKey] || 0)
      : bidCountByCrewKey[crewIdKey] || 0;
    const mergedLinkups = Math.max(existingLinkups, liveLinkups);
    const mergedBids = Math.max(existingBids, liveBids);

    return {
      ...crew,
      linkUps_nums: mergedLinkups,
      ticket_bids_num: mergedBids,
      linkUpsCount: mergedLinkups,
      ticketBidsCount: mergedBids,
    };
  });
};

const createFormattedCrewSelector = rawSelector =>
  createSelector([rawSelector, selectRawLinkups, selectAllBids], (crews, linkups, bids) =>
    transformCrewList(enrichCrewsWithLiveCounts(crews, linkups, bids))
  );

export const selectFormattedUserCrews = createFormattedCrewSelector(selectRawUserCrews);

export const selectFormattedFeaturedCrews = createSelector([selectRawFeaturedCrews], crews =>
  transformCrewList(crews || [])
);

export const selectFormattedCrewDetails = createSelector(
  [selectRawCrewDetails, selectRawLinkups, selectAllBids],
  (crew, linkups, bids) => {
    const enrichedCrew = enrichCrewsWithLiveCounts(crew ? [crew] : [], linkups, bids)[0] || null;
    return transformCrewData(enrichedCrew, { includeFullDetails: true });
  }
);

export const selectFormattedSearchResults = createFormattedCrewSelector(selectRawSearchResults);

export const selectFormattedUserMemberships = createSelector(
  [selectRawUserMemberships],
  memberships => memberships.map(formatMembership)
);

export const selectHasCrewMemberships = createSelector(
  [selectRawUserMemberships],
  memberships => memberships.length > 0
);

export const selectCrewMembershipIds = createSelector([selectRawUserMemberships], memberships =>
  memberships
    .map(membership =>
      typeof membership === 'string'
        ? membership
        : membership.crew_id || membership.crewId || membership.id
    )
    .filter(Boolean)
);

export const selectCrewMembershipIdsKey = createSelector([selectCrewMembershipIds], membershipIds =>
  membershipIds.join(',')
);

export const selectShouldShowFeaturedCrewSkeletons = createSelector(
  [selectFormattedFeaturedCrews, selectCrewsState],
  (featuredCrews, crewsState) =>
    featuredCrews.length === 0 &&
    crewsState.fetchFeaturedCrewsInProgress &&
    !crewsState.featuredCrewsLastFetched &&
    !crewsState.fetchFeaturedCrewsError
);
